import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { isEmpty } from "lodash";
import { DataContext } from "../../../context/DataContext";
import { getRuta, isAccess, simpleMessage } from "../../../helpers/Helpers";
import { deleteToken, deleteUserData, getToken } from "../../../api/Account";
import NoData from "../../../components/NoData";
import {
  deleteCommunityAsync,
  getMunicipalityByIdAsync,
} from "../../../api/Locations";
import SearchField from "../../../components/ui/SearchField";
import ListFooter from "../../../components/ui/ListFooter";
import BackButton from "../../../components/ui/BackButton";
import SmallModal from "../../../components/modals/SmallModal";

import Swal from "sweetalert2";
import CommunityAdd from "./communities/CommunityAdd";
import { CommunityEdit } from "./communities/CommunityEdit";
import { getTokens } from "../../../theme";

export const Communities = () => {
  let ruta = getRuta();
  const {
    setIsLoading,
    reload,
    setReload,
    setIsLogged,
    access,
    setIsUnautorized,
    isDarkMode,
  } = useContext(DataContext);
  const t = getTokens(isDarkMode);

  const token = getToken();
  let navigate = useNavigate();
  const { id } = useParams();

  const [municipality, setMunicipality] = useState([]);
  const [communityList, setCommunityList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const withSearch = communityList.filter((val) => {
    if (searchTerm === "") {
      return val;
    } else if ((val.name || "").toString().includes(searchTerm)) {
      return val;
    }
    return false;
  });

  //Para la paginacion
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = withSearch.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getMunicipalityByIdAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setMunicipality(result.data.result);
      setCommunityList(result.data.result.communities);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const deleteCommunity = async (item) => {
    Swal.fire({
      title: "¿Estas Seguro?",
      text: "No podras revertir esto...!",
      icon: "warning",
      showCancelButton: true,

      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#d83a3f",

      confirmButtonText: "Eliminar!",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "border",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        (async () => {
          setIsLoading(true);
          const result = await deleteCommunityAsync(token, item.id);
          if (!result.statusResponse) {
            setIsLoading(false);
            if (result.error === "Unauthorized") {
              setIsUnautorized(true);
              return;
            }
            simpleMessage(result.error, "error");
            return;
          }

          if (result.error === "eX01") {
            setIsLoading(false);
            deleteUserData();
            deleteToken();
            setIsLogged(false);
            return;
          }
          setIsLoading(false);
          setReload(!reload);

          Swal.fire("Eliminado!", "Comunidad Eliminada.", "success");
        })();
      }
    });
  };

  const onChangeSearch = (val) => {
    setCurrentPage(1);
    setSearchTerm(val.toUpperCase());
    paginate(1);
  };

  const actionButtonSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${t.fieldLine}`,
    backgroundColor: t.field,
  };

  const puedeAccionar =
    isAccess(access, "CONFIG UPDATE") || isAccess(access, "CONFIG DELETE");

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <BackButton
              onClick={() =>
                navigate(
                  `${ruta}/department/${
                    municipality.department ? municipality.department.id : ""
                  }`
                )
              }
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: 16.5,
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {municipality.name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.disabled", marginTop: "3px" }}>
                Comunidades del municipio
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
          >
            <SearchField
              value={searchTerm}
              onChange={onChangeSearch}
              placeholder="Buscar comunidad…"
              width={260}
            />
            {isAccess(access, "CONFIG CREATE") ? (
              <Button
                onClick={() => setShowModal(true)}
                startIcon={<AddRoundedIcon />}
                variant="contained"
                color="primary"
                sx={{ whiteSpace: "nowrap", height: "44px" }}
              >
                Agregar comunidad
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Comunidad</TableCell>
                    {puedeAccionar ? <TableCell align="center">Acciones</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        {row.id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.name}</TableCell>
                      {puedeAccionar ? (
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {isAccess(access, "CONFIG UPDATE") ? (
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionButtonSx, color: "warning.main" }}
                                  onClick={() => {
                                    setSelectedCommunity(row);
                                    setShowEditModal(true);
                                  }}
                                >
                                  <EditRoundedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {isAccess(access, "CONFIG DELETE") ? (
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionButtonSx, color: "error.main" }}
                                  onClick={() => deleteCommunity(row)}
                                >
                                  <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={withSearch}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="comunidades"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={"Agregar Comunidad"}
        isVisible={showModal}
        setVisible={setShowModal}
      >
        <CommunityAdd setShowModal={setShowModal} idMunicipality={id} />
      </SmallModal>

      <SmallModal
        titulo={`Editar: ${selectedCommunity.name}`}
        isVisible={showEditModal}
        setVisible={setShowEditModal}
      >
        <CommunityEdit
          selectedCommunity={selectedCommunity}
          setShowModal={setShowEditModal}
        />
      </SmallModal>
    </div>
  );
};
